import { supportedLanguages, type Language } from '@up2d8/contracts';
import nl from './locales/nl.json';
import fr from './locales/fr.json';
import en from './locales/en.json';

/**
 * De vertalingen: de drie hoofdbestanden plus de deelbestanden van het beheerportaal (BHR-0).
 *
 * Acht schermen van het beheer worden tegelijk herontworpen, elk door iemand anders. Zouden ze
 * allemaal in `nl.json` schrijven, dan botst elke samenvoeging op dezelfde regels. Daarom heeft elk
 * deel een eigen bestand per taal: `locales/beheer/<naam>.<taal>.json`, bv. `04-berichten.fr.json`.
 * Ze worden hier vanzelf opgepikt en diep samengevoegd met het hoofdbestand van die taal; een nieuw
 * bestand hoeft nergens geregistreerd te worden.
 *
 * Een sleutel die al bestaat, wordt **niet** overschreven: een deelbestand voegt toe, het verandert
 * niets wat een ander scherm gebruikt. `resources.test.ts` faalt als dat toch gebeurt, en ook als
 * een taal een sleutel mist die een andere wel heeft.
 */

/** Een vertaling is tekst, een lijst teksten (`returnObjects`, bv. `privacy.seenList`) of een tak. */
export type Messages = { [key: string]: string | string[] | Messages };

/** Een tak, geen blad: een object dat geen lijst is. */
export function isBranch(value: unknown): value is Messages {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const extraFiles = import.meta.glob<Messages>('./locales/beheer/*.json', {
  eager: true,
  import: 'default',
});

export interface ExtraFile {
  /** Het pad zoals `import.meta.glob` het geeft, voor foutmeldingen. */
  path: string;
  /** Het deel, bv. `00-basis`. */
  name: string;
  language: Language;
  messages: Messages;
}

const FILE_PATTERN = /\/([^/]+)\.([a-z]{2})\.json$/;

/** De deelbestanden, met naam en taal uit de bestandsnaam. Een onbekende taal is een fout. */
export function extraLocaleFiles(): ExtraFile[] {
  return Object.entries(extraFiles).map(([path, messages]) => {
    const match = FILE_PATTERN.exec(path);
    const language = match?.[2];
    if (!match || !language || !(supportedLanguages as readonly string[]).includes(language)) {
      throw new Error(
        `Vertaalbestand ${path} heeft geen geldige naam: verwacht <naam>.<${supportedLanguages.join('|')}>.json`,
      );
    }
    return { path, name: match[1] as string, language: language as Language, messages };
  });
}

/**
 * `extra` in `base`, diep. Bestaat een sleutel al, dan blijft de waarde van `base` staan en komt
 * het pad in `conflicts`; dat is wat de test vangt.
 */
export function deepMerge(
  base: Messages,
  extra: Messages,
  conflicts: string[] = [],
  prefix = '',
): Messages {
  const result: Messages = { ...base };
  for (const [key, value] of Object.entries(extra)) {
    const path = prefix ? `${prefix}.${key}` : key;
    const existing = result[key];
    if (existing === undefined) {
      result[key] = value;
    } else if (isBranch(existing) && isBranch(value)) {
      result[key] = deepMerge(existing, value, conflicts, path);
    } else {
      conflicts.push(path);
    }
  }
  return result;
}

export const baseMessages: Record<Language, Messages> = {
  nl: nl satisfies Messages,
  fr: fr satisfies Messages,
  en: en satisfies Messages,
};

/** De volledige vertaling per taal; `conflicts` krijgt de sleutels die dubbel bestonden. */
export function buildMessages(conflicts: string[] = []): Record<Language, Messages> {
  const result = { ...baseMessages };
  // Op naam gesorteerd, zodat de volgorde niet van het bestandssysteem afhangt.
  const files = extraLocaleFiles().sort((a, b) => a.path.localeCompare(b.path));
  for (const file of files) {
    const found: string[] = [];
    result[file.language] = deepMerge(result[file.language], file.messages, found);
    conflicts.push(...found.map((key) => `${file.path}: ${key}`));
  }
  return result;
}
